function readAuthToken() {
  try {
    return localStorage.getItem('auth_token');
  } catch (_) {
    return null;
  }
}

function sanitizeMetadata(value, depth = 0) {
  if (depth > 4) {
    return '[MAX_DEPTH]';
  }

  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === 'string') {
    return value.length > 2000 ? `${value.slice(0, 2000)}…[TRUNCATED]` : value;
  }

  if (typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.slice(0, 50).map((entry) => sanitizeMetadata(entry, depth + 1));
  }

  const redacted = {};
  const sensitivePattern = /password|token|secret|authorization|cookie|apikey|api_key|access_token|refresh_token|client_secret/i;

  for (const [key, entry] of Object.entries(value)) {
    redacted[key] = sensitivePattern.test(key)
      ? '[REDACTED]'
      : sanitizeMetadata(entry, depth + 1);
  }

  return redacted;
}

export function generateRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `req_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeError(error, fallbackMessage) {
  if (error instanceof Error) {
    return error;
  }

  if (error && typeof error === 'object') {
    const normalized = new Error(error.message || fallbackMessage || 'Unknown error');
    Object.assign(normalized, error);
    return normalized;
  }

  return new Error(fallbackMessage || String(error || 'Unknown error'));
}

export async function reportClientError({
  message,
  error,
  severity = 'error',
  category = 'client_runtime',
  requestId = null,
  gameSessionId = null,
  conversationId = null,
  httpStatus = null,
  provider = null,
  metadata = {},
} = {}) {
  const normalizedError = normalizeError(error, message);
  const token = readAuthToken();

  const payload = {
    message: message || normalizedError.message || 'Frontend error event',
    severity,
    category,
    requestId,
    gameSessionId,
    conversationId,
    httpStatus,
    provider,
    route: typeof window !== 'undefined' ? window.location.pathname : null,
    url: typeof window !== 'undefined' ? window.location.href : null,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    error: {
      message: normalizedError.message,
      name: normalizedError.name,
      stack: normalizedError.stack,
      code: normalizedError.code || null,
    },
    metadata: sanitizeMetadata(metadata),
  };

  try {
    await fetch('/.netlify/functions/log-error-event', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(token ? { ...payload, token } : payload),
      keepalive: true,
    });
  } catch (reportingError) {
    console.error('Failed to report client error:', reportingError);
  }
}

export function installGlobalErrorHandlers() {
  if (typeof window === 'undefined' || window.__cqGlobalErrorHandlersInstalled) {
    return;
  }

  window.__cqGlobalErrorHandlersInstalled = true;

  window.addEventListener('error', (event) => {
    reportClientError({
      message: event.message || 'Unhandled window error',
      error: event.error || new Error(event.message || 'Unhandled window error'),
      severity: 'fatal',
      category: 'window_error',
      metadata: {
        filename: event.filename || null,
        lineno: event.lineno || null,
        colno: event.colno || null,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    reportClientError({
      message: 'Unhandled promise rejection',
      error: event.reason,
      severity: 'error',
      category: 'unhandled_rejection',
    });
  });
}
